// Health.jsx — Unified Health Monitor (Session 50 redesign).
// Binary 🟢 OK / 🔴 Discrepancy. Every problem is a row with an action: a red is
// handled by picking ONE of Fix / Retry / Ignore, then clicking Close (which tags
// the green ✓ with the reason). "Ask AI" only advises — it never resolves anything.
// The old separate Discrepancies tab is folded in here (rows M5 + E15).

import React, { useState, useEffect, useCallback } from 'react';

const GROUPS = [
  { key: 'morning', label: 'Morning · 9:10 AM' },
  { key: 'weekly', label: 'Weekly · Monday' },
  { key: 'evening', label: 'Evening · 7:05 PM' },
];

const BTN = (bg, color, border) => ({
  fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 4,
  background: bg, color, border: border || 'none', cursor: 'pointer', whiteSpace: 'nowrap',
});

function Pill({ lifecycle, resolution }) {
  if (lifecycle === 'red_open')
    return <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: '#3a1a1a', color: '#fca5a5', border: '1px solid #991b1b', whiteSpace: 'nowrap' }}>🔴 Discrepancy</span>;
  if (lifecycle === 'red_resolved')
    return <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: '#0d2818', color: '#86efac', border: '1px solid #166534', whiteSpace: 'nowrap' }}>🟢 ✓ {resolution || 'Resolved'}</span>;
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: '#0d2818', color: '#86efac', border: '1px solid #166534', whiteSpace: 'nowrap' }}>🟢 OK</span>;
}

function Row({ item, busy, onAction, onClose, onAskAI, ai }) {
  const [open, setOpen] = useState(false);
  const isOpenRed = item.lifecycle === 'red_open';
  const actions = item.actions || [];
  const picked = item.chosen_action;
  const canClose = !item.is_discrepancy && !!picked;
  const hasDetail = item.metadata || ai || item.ai_advice;

  return (
    <div style={{ borderBottom: '1px solid #1a1d24', padding: '10px 14px', background: isOpenRed ? '#1c1416' : 'transparent' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Pill lifecycle={item.lifecycle} resolution={item.resolution} />
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b7280', minWidth: 34 }}>{item.check_code}</span>
        <span style={{ fontSize: 13, color: '#e5e7eb', fontWeight: 600 }}>{item.title}</span>
        <span style={{ fontSize: 12, color: '#9ca3af', flex: 1, minWidth: 180 }}>{item.detail}</span>

        {isOpenRed && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {actions.includes('retry') && (
              <button disabled={busy} onClick={() => onAction(item, 'retry')}
                style={BTN(picked === 'retry' ? '#1d4ed8' : '#1f2937', picked === 'retry' ? '#fff' : '#93c5fd', '1px solid #1d4ed8')}>Retry</button>
            )}
            {actions.includes('fix') && (
              <button disabled={busy} onClick={() => onAction(item, 'fix')}
                style={BTN(picked === 'fix' ? '#15803d' : '#1f2937', picked === 'fix' ? '#fff' : '#86efac', '1px solid #166534')}>Fix</button>
            )}
            {actions.includes('ask_ai') && (
              <button disabled={busy} onClick={() => onAskAI(item)}
                style={BTN('#1f2937', '#c4b5fd', '1px solid #5b21b6')}>Ask AI</button>
            )}
            {actions.includes('ignore') && (
              <button disabled={busy} onClick={() => onAction(item, 'ignore')}
                style={BTN(picked === 'ignore' ? '#92400e' : '#1f2937', picked === 'ignore' ? '#fff' : '#fbbf24', '1px solid #92400e')}>Ignore</button>
            )}
            {!item.is_discrepancy && (
              <button disabled={busy || !canClose} onClick={() => onClose(item)} title={canClose ? 'Close this red' : 'Pick Fix, Retry or Ignore first'}
                style={{ ...BTN(canClose ? '#374151' : '#16181f', canClose ? '#f9fafb' : '#4b5563', '1px solid #374151'), cursor: canClose ? 'pointer' : 'not-allowed' }}>Close ✓</button>
            )}
          </div>
        )}
        {hasDetail && (
          <button onClick={() => setOpen(!open)} style={{ ...BTN('transparent', '#6b7280', '1px solid #374151') }}>{open ? '▾' : '▸'}</button>
        )}
      </div>

      {picked && isOpenRed && !item.is_discrepancy && (
        <div style={{ marginTop: 6, marginLeft: 46, fontSize: 11, color: '#9ca3af' }}>
          Picked <b style={{ color: '#d1d5db' }}>{picked}</b> — click <b style={{ color: '#d1d5db' }}>Close ✓</b> to clear it.
        </div>
      )}

      {open && (
        <div style={{ marginTop: 8, marginLeft: 46, padding: 10, background: '#0f1117', borderRadius: 4, fontSize: 11, color: '#d1d5db' }}>
          {(ai || item.ai_advice) && (
            <div style={{ marginBottom: item.metadata ? 10 : 0, padding: 8, background: '#1a1530', border: '1px solid #5b21b6', borderRadius: 4 }}>
              <div style={{ fontSize: 10, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>AI advice (advisory only)</div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{ai || item.ai_advice}</div>
            </div>
          )}
          {item.metadata && (
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 10, color: '#9ca3af' }}>{JSON.stringify(item.metadata, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}

export default function Health({ sessionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [aiByRow, setAiByRow] = useState({});

  const headers = useCallback(() => ({ 'x-session-id': sessionId || '', 'Content-Type': 'application/json' }), [sessionId]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch('/api/admin/health-checks', { headers: headers() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const doAction = async (item, action) => {
    setBusyId(item.id); setError(null); setInfo(null);
    try {
      const r = await fetch(`/api/admin/health-checks/${encodeURIComponent(item.id)}/action`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ action }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.message) setInfo(`${action}: ${j.message}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const doClose = async (item) => {
    setBusyId(item.id); setError(null); setInfo(null);
    try {
      const r = await fetch(`/api/admin/health-checks/${encodeURIComponent(item.id)}/close`, { method: 'POST', headers: headers() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setInfo(`Closed — ${j.resolution || 'resolved'}`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const doAskAI = async (item) => {
    setBusyId(item.id); setError(null); setInfo(null);
    try {
      const r = await fetch(`/api/admin/health-checks/${encodeURIComponent(item.id)}/ask-ai`, { method: 'POST', headers: headers() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      // Confidence-gated (discrepancy rows): a CONFIDENT result has no advice text — it
      // persisted a proposal, so show its message and reload to reveal the Fix button.
      // Otherwise show the advice/"couldn't determine" line inline on the row.
      if (j.confident && j.message) setInfo(j.message);
      else if (j.advice) setAiByRow(prev => ({ ...prev, [item.id]: j.advice }));
      await load(); // a confident discrepancy proposal surfaces a Fix button
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const refreshHolidays = async () => {
    setBusyId('holidays'); setError(null); setInfo(null);
    try {
      const r = await fetch('/api/admin/health-checks/refresh-holidays', { method: 'POST', headers: headers() });
      const j = await r.json();
      if (j.ok || j.count != null) setInfo(`NSE holidays refreshed: ${j.count} entries`);
      else throw new Error(j.error || 'failed');
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const rows = (data && data.rows) || [];
  const counts = (data && data.counts) || { red_open: 0, red_resolved: 0, ok: 0, total: 0 };

  return (
    <div style={{ padding: 16, color: '#f9fafb', background: '#0f1117', minHeight: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Health</h2>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>Every check is green or red. Each red needs an action — Fix, Retry or Ignore — then Close.</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button disabled={busyId === 'holidays'} onClick={refreshHolidays}
            style={{ padding: '6px 14px', fontSize: 12, background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: 4, cursor: 'pointer' }}>Refresh NSE holidays</button>
          <button disabled={loading} onClick={load}
            style={{ padding: '6px 14px', fontSize: 12, background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: 4, cursor: 'pointer' }}>Reload</button>
        </div>
      </div>

      {/* Banner */}
      <div style={{
        padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 14, fontWeight: 600,
        background: counts.red_open > 0 ? '#3a1a1a' : '#0d2818',
        border: `1px solid ${counts.red_open > 0 ? '#991b1b' : '#166534'}`,
        color: counts.red_open > 0 ? '#fca5a5' : '#86efac',
      }}>
        {counts.red_open > 0
          ? `🔴 ${counts.red_open} discrepanc${counts.red_open === 1 ? 'y needs' : 'ies need'} your attention`
          : '🟢 All clear — nothing to do'}
        <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 10, color: '#9ca3af' }}>
          {counts.ok} OK · {counts.red_resolved} resolved · {counts.total} total
          {data && data.generated_at ? ` · updated ${data.generated_at}` : ''}
        </span>
      </div>

      {error && <div style={{ padding: 10, background: '#3a1a1a', border: '1px solid #991b1b', color: '#fca5a5', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>{error}</div>}
      {info && <div style={{ padding: 10, background: '#0d2818', border: '1px solid #166534', color: '#86efac', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>{info}</div>}

      {loading && !data && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>}

      {data && GROUPS.map(g => {
        const groupRows = rows.filter(r => r.group === g.key);
        if (groupRows.length === 0) return null;
        const reds = groupRows.filter(r => r.lifecycle === 'red_open').length;
        return (
          <div key={g.key} style={{ marginBottom: 18, background: '#1a1d24', border: '1px solid #374151', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: '#13161d', borderBottom: '1px solid #374151', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              {g.label}
              <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 11 }}>({groupRows.length})</span>
              {reds > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: '#fca5a5', background: '#3a1a1a', border: '1px solid #991b1b', borderRadius: 4, padding: '1px 7px' }}>{reds} red</span>}
            </div>
            {groupRows.map(it => (
              <Row key={it.id} item={it} busy={busyId === it.id} ai={aiByRow[it.id]}
                onAction={doAction} onClose={doClose} onAskAI={doAskAI} />
            ))}
          </div>
        );
      })}

      {data && rows.length === 0 && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          No checks have run yet. They run automatically at 9:10 AM and 7:05 PM IST on trading days.
        </div>
      )}
    </div>
  );
}
